import { IHoldingsRequest, IHoldingsExposuresDataResult, IAssetDrilldownRequest, SecuritySubType, InvestorAction } from "src/app/models/holdings";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { BaseHttpService } from "./base.http.service";
import { Injectable } from "@angular/core";
import { CachedHttpObservableService } from "./cached.http-observable.service";
import { AppConfig } from 'src/app/app.config';
import { urlToHttpOptions } from "url";
import { IEnvironmentConfig } from "src/app/models/environmentConfig";

export interface IHoldingChart{
  securityId: number;
  date: string;
  entities: string;
}

@Injectable()
export class HoldingsService extends BaseHttpService {
  constructor(
    private http: HttpClient,
    private cachedHttpObservableService: CachedHttpObservableService,
    private appConfig: AppConfig
  ) {
    super();
  }

  get envSettings(): IEnvironmentConfig {
    return this.appConfig.getEnvironmentConfig();
  }

  public GetHoldingsValuationData(paramsObject: IHoldingsRequest): Observable<any> {
    const url = new URL(`${this.envSettings.webApiUrl}portfolios/valuations`);

    const date = paramsObject.date !== undefined
        ? this.parseDate(paramsObject.date)
        : this.parseDate(new Date());

    url.searchParams.set('date', date.toString());

    paramsObject.clientEntityIds.forEach(item => {
      url.searchParams.append('entities', item.toString());
    });

    paramsObject.portfolioIds?.forEach(item => {
      url.searchParams.append('portfolios', item.toString());
    });

    return this.cachedHttpObservableService.getCachedHttpGetObservable(url.toString(), this.http);
  }

  public GetHoldingsAllocationsData(paramsObject: IHoldingsExposuresDataResult): Observable<any> {
    const url = new URL(`${this.envSettings.webApiUrl}portfolios/allocations`);

    const date = paramsObject.date !== undefined
        ? this.parseDate(paramsObject.date)
        : this.parseDate(new Date());

    url.searchParams.set('date', date.toString());

    const entityIds = paramsObject.clientEntities.map(selectedEntity => selectedEntity.EntityId);
    entityIds.forEach(item => {
      url.searchParams.append('entities', item.toString());
    });

    if (paramsObject.portfolios && paramsObject.portfolios.length > 0) {
      const portfolioIds = paramsObject.portfolios.map(x => x.id);
      portfolioIds.forEach(item => {
        url.searchParams.append('portfolios', item.toString());
      });
    }

    return this.http.get(url.toString(), {
      observe: "body",
      responseType: "json",
      headers: this.getHttpHeaders()
    });
  }

  public GetHoldingsExposureData(paramsObject: IHoldingsRequest): Observable<any> {
    const url = new URL(`${this.envSettings.webApiUrl}portfolios/exposures`);

    const date = paramsObject.date !== undefined
        ? this.parseDate(paramsObject.date)
        : this.parseDate(new Date());

    url.searchParams.set('date', date.toString());

    paramsObject.clientEntityIds.forEach(item => {
      url.searchParams.append('entities', item.toString());
    });

    paramsObject.portfolioIds?.forEach(item => {
      url.searchParams.append('portfolios', item.toString());
    });

    return this.cachedHttpObservableService.getCachedHttpGetObservable(url.toString(), this.http);
  }

  public GetAssetPerformance(paramsObject: IAssetDrilldownRequest): Observable<any> {
    let url: string;
    let entityQueryString = "";

    switch (paramsObject.securitySubType) {
      case SecuritySubType.EQUITY:
        url = `${this.envSettings.webApiUrl}securities/${paramsObject.assetId}/quotes/series`;
        break;
      case SecuritySubType.PRIVATE_EQUITY:
        const date = paramsObject.date !== undefined
            ? this.parseDate(paramsObject.date)
            : this.parseDate(new Date());

        paramsObject.entityIds.forEach(item => {
          entityQueryString += "&entities=" + item;
        });
        url = `${this.envSettings.webApiUrl}privequity/summary/fund/${paramsObject.assetId}/asat/${date}?periodType=Year${entityQueryString}`;
        break;
      case SecuritySubType.REAL_ESTATE:
        url = ``;
        break;
      case SecuritySubType.FOREIGNEXCHANGEFORWARD:
        paramsObject.entityIds.forEach(item => {
          entityQueryString += "&entities=" + item;
        });
        url = `${this.envSettings.webApiUrl}portfolios/ffxSummary?securityId=${paramsObject.assetId}${entityQueryString}`;
        break;
      default:
        url = `${this.envSettings.webApiUrl}securities/${paramsObject.assetId}/quotes/series`;
        break;
    }

    return this.http.get(url, {
      observe: "body",
      responseType: "json",
      headers: this.getHttpHeaders()
    });
  }
  
  public GetInvestorActions(paramsObject: IAssetDrilldownRequest): Observable<InvestorAction[]> {
    const entityString = paramsObject.entityIds.join("&entities=");
    const date = paramsObject.date != null
      ? this.parseDate(paramsObject.date)
      : this.parseDate(new Date());

    let url = `${this.envSettings.webApiUrl}portfolios/holdings/activity?securityId=${paramsObject.assetId}&entities=${entityString}&tillDate=${date}`;

    if (paramsObject.portfolioIds && paramsObject.portfolioIds.length > 0)
    {
      url = url.concat("&portfolios=");
      const portfolioString = paramsObject.portfolioIds.join("&portfolios=");
      url = url.concat(portfolioString);
    }

    return this.http.get<InvestorAction[]>(url, {
      headers: this.getHttpHeaders()
    });
  }

  public GetHoldingsChartData(paramsObject: IHoldingChart): Observable<any> {
    const entityString = paramsObject.entities.split(',').join('&entities=');

    const url = `${this.envSettings.webApiUrl}portfolio/fundholdings?fundSecurityId=${paramsObject.securityId}&asAtDate=${paramsObject.date}&entities=${entityString}`;

    return this.http.get<any>(url, {
      headers: this.getHttpHeaders()
    });
  }
}